/* eslint-disable no-nested-ternary */
import {
  BooleanParam,
  DateParam,
  StringParam,
  useQueryParam,
} from 'use-query-params';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ArrowContainer } from 'react-tiny-popover';
import { CachePolicies, useFetch } from 'use-http';
import { DownIcon } from '@/features/details/date-pick/chip';
import { VSpace } from '@/components/spacing';
import { CircularCheckmark } from '@/components/circular-checkmark';
import { Tooltip } from '@/components/tooltip';
import { BigButton } from '@/components/big-button';
import { Chip } from './shared';
import { routes } from '@/webapi/routes';
import { maybe } from '@/features/details/utils';
import { ResponsivePopover } from '@/components/responsive-popover';
import { breakpoints } from '@/components/responsive';

export function SessionTypeChip({
  expId,
  metricsStart,
  whiteBg,
}: {
  expId: string;
  metricsStart?: string;
  whiteBg?: boolean;
}) {
  const containerZIndex = 1000;
  const { put, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
    },
  }));

  const [sessionsV2, setSessionsV2] = useQueryParam<string>(`st`, StringParam);
  const [current, setCurrent] = useState<string>(sessionsV2 || ``);

  const [applyToAllReports, setApplyToAllReports] = useQueryParam<boolean>(
    `ap`,
    BooleanParam,
  );
  const [currApplyToAllReports, setCurrApplyToAllReports] = useState<boolean>(
    !!applyToAllReports,
  );
  const [open, setOpen] = useState(false);
  const notAvailable = useIsNotAvailable(metricsStart);
  useEffect(() => {
    if (notAvailable) {
      setSessionsV2(``);
      setCurrent(``);
      setApplyToAllReports(false);
      setCurrApplyToAllReports(false);
    }
  }, [notAvailable]);
  const onSave = async () => {
    setSessionsV2(current);
    setApplyToAllReports(currApplyToAllReports);
    await put(routes.saveExperienceSessionsType, {
      applySessionTypeOnAllCharts: currApplyToAllReports,
      sessionType: current,
      experienceId: expId,
    });
    setOpen(false);
  };
  if (notAvailable) {
    return null;
  }
  return (
    <ResponsivePopover
      onClickOutside={() => setOpen(!open)}
      isOpen={open}
      containerStyle={{ zIndex: `${containerZIndex}` }}
      positions={[`bottom`, `left`, `right`]}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={20}
        >
          <PopoverContainer>
            <Row>
              <CircularCheckmark
                onChange={() => {
                  setCurrent(``);
                }}
                selected={current === ``}
                caption="Sessions"
              />
              <Tooltip
                title="Sessions"
                text="We count the sessions of visitors who received the experience. All subsequent sessions of the same visitors are also counted, even if, in later sessions, they were not exposed to the experience again (during the experiment run time)."
                zIndex={containerZIndex + 1}
              />
            </Row>
            <VSpace value={2} />
            <Row>
              <CircularCheckmark
                onChange={(sel) => {
                  setCurrent(sel ? `sessions` : ``);
                }}
                selected={current === `sessions`}
                caption="Sessions with impressions"
              />
              <Tooltip
                title="Sessions with impressions"
                text={SessionsWithImpressionsTooltipText}
                zIndex={containerZIndex + 1}
              />
            </Row>
            <VSpace value={2} />
            <Row>
              <CircularCheckmark
                onChange={(sel) => {
                  setCurrent(sel ? `users` : ``);
                }}
                selected={current === `users`}
                caption="Users"
              />
              <Tooltip
                title="Users"
                text={UsersTooltipText}
                zIndex={containerZIndex + 1}
              />
            </Row>
            <VSpace value={3} />
            <BigButton
              disabled={loading}
              onClick={onSave}
              color="white"
              background="#3451fa"
              noTransform
              fillWidth
              type="submit"
              size="medium"
            >
              {loading ? `...` : `Save`}
            </BigButton>
            <VSpace value={2} />
          </PopoverContainer>
        </ArrowContainer>
      )}
    >
      <Container>
        <Separator>|</Separator>
        <Chip whiteBg={whiteBg} onClick={() => setOpen(!open)}>
          <ChipText> {getChipText(sessionsV2)}</ChipText>
          <DownIcon />
        </Chip>
      </Container>
    </ResponsivePopover>
  );
}

export const ChipText = styled.div`
  display: inline-block;
  text-align: start;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 8rem;
  height: 100%;
  white-space: nowrap;
  text-transform: capitalize;
`;
export const Row = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
`;
export const PopoverContainer = styled.div`
  border-radius: 10px;
  background-color: white;
  padding: 3rem;
  border: solid 1px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);

  ${breakpoints.down(`md`)} {
    width: 100%;
    border-radius: 2rem 2rem 0 0;
  }
`;
export const Separator = styled.span`
  padding: 0 0.8rem 0 0;
  user-select: none;
  font-family: 'JetBrains Mono', serif;
  font-size: 2.2rem;
  color: #dedfe0;
  text-align: center;
  vertical-align: center;
`;
export const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

function getChipText(sessionsV2: string | undefined) {
  return sessionsV2 === `` || typeof sessionsV2 === `undefined`
    ? `sessions`
    : sessionsV2 === `users`
    ? `users`
    : `sessions with impression`;
}

function useIsNotAvailable(metricsStart: string) {
  if (
    window.location.hostname === `pre-prod--kind-noether-230989.netlify.app`
  ) {
    return false;
  }
  try {
    const availableSince = new Date(`2024-04-28T00:00:00`);
    const [queryStart] = useQueryParam(`s`, DateParam);
    const metricsStartAt = maybe(() => new Date(metricsStart).valueOf());
    return !(
      (!queryStart || queryStart.valueOf() >= availableSince.valueOf()) &&
      (metricsStartAt >= availableSince.valueOf() || true)
    );
  } catch (e) {
    return true;
  }
}

export const UsersTooltipText = `Identified visitors are counted. If visitors receive the experience and return the next day or re-engage after 30 minutes of their last action, they are still counted as one user.`;
export const SessionsWithImpressionsTooltipText = `We count the sessions of visitors who received the experience, and subsequent sessions of the same visitor are only counted if they were exposed to the experience again in those sessions (during the experiment run time).`;
